import { useFeatureFlag } from "@/shared/hooks/useFeatureFlag";
import { env } from "@/shared/config/env";

/**
 * Per-station WorkOS flag gating incident management. Unchanged from when the
 * feature lived in this app — enabling it for a station still turns the
 * capability on for that station's users at their next login, it just now
 * reveals a link to the Investigator client rather than a local route.
 */
export const INCIDENT_MANAGEMENT_FEATURE_FLAG = "incident-management";

export interface InvestigatorAppLink {
  /** Whether to surface the entry point at all. */
  readonly enabled: boolean;
  /** Absolute origin of the Investigator client; defined whenever enabled. */
  readonly url: string | undefined;
}

/**
 * Whether to show the Investigator entry point, and where it points.
 *
 * Two gates, BOTH required — the same shape the deleted
 * `useIncidentManagementEnabled` had, with the build-time precondition swapped
 * from "the incidents API is configured" to "the Investigator client's origin
 * is configured", since this app no longer calls that API itself:
 *
 *  1. `VITE_INVESTIGATOR_APP_URL` is set for this build. Without it the only
 *     alternative is a dead link, so the entry is hidden instead.
 *  2. the station's WorkOS `incident-management` flag.
 */
export function useInvestigatorApp(): InvestigatorAppLink {
  // Called unconditionally (Rules of Hooks), then ANDed with the precondition.
  const flagOn = useFeatureFlag(INCIDENT_MANAGEMENT_FEATURE_FLAG);
  const url = env.apps.investigator;
  return { enabled: Boolean(url) && flagOn, url };
}
